import {useTransition} from 'react';
import {Send, SendHorizontal, MailWarning, RefreshCw, X} from 'lucide-react';
import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  CopyButton
} from '@kroll-resolver-crisp/ui-components-react';
import {type Submission, submissionsApi} from '@/api/submissions';
import {type HopperConfig, hopperConfigsApi} from '@/api/hopper-configs';
import {useApolloRelayConfig} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {useConfirm} from '@/components/shared/useConfirm';
import {findHopperConfig} from '@/components/submissions/findHopperConfig';
import {BulkFlagMenu} from '@/components/flags/Flag';
import {type FlagColor} from '@/api/flags';
import {showToast} from '@/lib/toast';

// The one bar for everything a multi-submission selection can do - it replaces both the old
// per-policy-violation-group Send/Send-to-Apollo header buttons and the separate "Create P0 Alert"
// bar, since dropping the per-PV grouping means there is no longer a group header to hang the
// hopper button off. It sits along the bottom edge of the page rather than floating over the grid.
export function SubmissionBulkBar({
  selectedSubmissions,
  hopperConfigs,
  onSent,
  onClear,
  onCreateP0,
  onSetFlag
}: {
  readonly selectedSubmissions: readonly Submission[];
  readonly hopperConfigs: readonly HopperConfig[];
  readonly onSent: (ids: string[]) => void;
  readonly onClear: () => void;
  readonly onCreateP0: () => void;
  // Flags (or with null, unflags) the whole selection in one request.
  readonly onSetFlag: (color: FlagColor | null) => void;
}) {
  const [sending, startSend] = useTransition();
  const {confirm, confirmDialog} = useConfirm();
  // Hidden rather than left to 422: an analyst cannot act on "Apollo is switched off", and the
  // switch that turns it back on lives in Admin -> Hoppers.
  const {data: apolloConfig} = useApolloRelayConfig();
  const apolloSending = apolloConfig?.sending ?? false;

  if (selectedSubmissions.length === 0) {
    return null;
  }

  // A historic row has no event stream, so the stream actions (hopper, Apollo, flag, P0) don't apply
  // to a selection that includes one; the status check and Copy IDs do.
  const historicSelected = selectedSubmissions.some((s) => s.is_historic);

  // A hopper fire() takes exactly one hopper, keyed on (policy_violation, submission_type) - a
  // selection spanning more than one of either has no single correct hopper to send to.
  const policyViolations = new Set(selectedSubmissions.map((s) => s.policy_violation));
  const types = new Set(selectedSubmissions.map((s) => s.submission_type));
  const [onlyPolicyViolation] = policyViolations;
  const [onlyType] = types;
  const hopperConfig =
    !historicSelected &&
    policyViolations.size === 1 &&
    types.size === 1 &&
    onlyPolicyViolation &&
    onlyType
      ? findHopperConfig(hopperConfigs, onlyPolicyViolation, onlyType)
      : null;
  const mixedSelectionReason = historicSelected
    ? 'Historic submissions cannot be sent'
    : policyViolations.size > 1
      ? 'Select submissions from one policy violation'
      : types.size > 1
        ? 'Select one submission type'
        : 'No hopper configured for this policy violation and type';

  async function handleSend() {
    if (!hopperConfig) {
      return;
    }

    const alreadySent = selectedSubmissions.filter((s) => s.action_status === 'sent to client');
    if (alreadySent.length > 0) {
      const ids = alreadySent.map((s) => s.item_external_id ?? s.content_id);
      const message =
        alreadySent.length === 1
          ? `"${ids[0] ?? ''}" has already been sent to the client.`
          : `${alreadySent.length} of the selected submissions have already been sent (${ids.slice(0, 3).join(', ')}${ids.length > 3 ? '…' : ''}).`;
      if (
        !(await confirm({
          title: 'Re-send anyway?',
          description: message,
          confirmLabel: 'Re-send',
          destructive: false
        }))
      ) {
        return;
      }
    }

    startSend(async () => {
      try {
        // Fire only enqueues submissions already sitting "in hopper" - an already-delivered one
        // has to be explicitly re-queued first (the same re-queue a failed delivery's Retry uses)
        // before it becomes eligible again, so this is a real two-step operation in one click.
        if (alreadySent.length > 0) {
          await Promise.all(alreadySent.map(async (s) => submissionsApi.retry(s.id)));
        }

        const ids = selectedSubmissions.map((s) => s.id);
        const sentResult = await hopperConfigsApi.fire(hopperConfig.id, ids);
        showToast(`Successfully sent ${sentResult.sent} item(s)`);
        onSent(ids);
      } catch (error) {
        showToast(getApiErrorMessage(error, 'Send operation failed'), 'error');
      }
    });
  }

  // Deliberately not a hopper fire: Apollo is a separate destination, so this never touches the
  // client-delivery state and needs no hopper config. Client-side loop over the per-submission
  // endpoint - no bulk endpoint exists or is needed.
  function handleSendToApollo() {
    startSend(async () => {
      const ids = selectedSubmissions.map((s) => s.id);
      const results = await Promise.allSettled(
        ids.map(async (id) => submissionsApi.sendToApollo(id))
      );
      const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      const firstRejection = rejected[0];
      const reason =
        firstRejection === undefined
          ? ''
          : getApiErrorMessage(firstRejection.reason, 'see the browser console');
      if (rejected.length === 0) {
        showToast(`Queued ${ids.length} item(s) for Apollo`);
      } else {
        showToast(
          `Queued ${ids.length - rejected.length} of ${ids.length} for Apollo — ${reason}`,
          'error'
        );
      }
    });
  }

  function handleStatusCheck() {
    startSend(async () => {
      try {
        const {queued} = await submissionsApi.requestStatusCheck(
          selectedSubmissions.map((s) => s.id)
        );
        const skipped = selectedSubmissions.length - queued;
        showToast(
          skipped === 0
            ? `Queued ${queued} status check(s) - results arrive within a few minutes`
            : `Queued ${queued} status check(s); ${skipped} can't be checked (not delivered, or no platform ID)`
        );
      } catch (error) {
        showToast(getApiErrorMessage(error, 'Status check request failed'), 'error');
      }
    });
  }

  // The platform content ids, one per line - what an analyst pastes into a client thread or a
  // spreadsheet, not PRI's own submission ids.
  function copyIds() {
    return selectedSubmissions.map((s) => s.item_external_id ?? s.content_id).join('\n');
  }

  // A white band over the grey content area, so the bar stands off the table it acts on. The
  // secondary buttons here (and the flag menu's, whose variant is not ours to set) need it: 3.x's
  // near-white secondary sat at 1.02:1 on a tinted surface and vanished into the bar.
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="border-border bg-card flex flex-wrap items-center gap-2 border-t px-5 py-2">
      <span className="text-xs font-medium whitespace-nowrap">
        {selectedSubmissions.length} selected
      </span>

      {hopperConfig ? (
        <Button size="sm" disabled={sending} onClick={handleSend}>
          {sending ? <Spinner /> : <Send />}
          Send to hopper
        </Button>
      ) : (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button size="sm" variant="secondary" aria-disabled="true" className="opacity-50" />
            }>
            <Send />
            Send to hopper
          </TooltipTrigger>
          <TooltipContent side="top">{mixedSelectionReason}</TooltipContent>
        </Tooltip>
      )}

      {apolloSending && !historicSelected ? (
        <Button variant="secondary" size="sm" disabled={sending} onClick={handleSendToApollo}>
          {sending ? <Spinner /> : <SendHorizontal />}
          Send to Apollo
        </Button>
      ) : null}

      {historicSelected ? null : (
        <BulkFlagMenu
          showClear={selectedSubmissions.some((s) => s.flag !== null)}
          onSelect={onSetFlag}
        />
      )}

      <Button variant="secondary" size="sm" disabled={sending} onClick={handleStatusCheck}>
        {sending ? <Spinner /> : <RefreshCw />}
        Status check
      </Button>

      <CopyButton variant="secondary" size="sm" label="Copy IDs" value={copyIds} />

      {historicSelected ? null : (
        <Button variant="secondary" size="sm" onClick={onCreateP0}>
          <MailWarning />
          Create P0 alert
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Clear selection"
        className="ml-auto"
        onClick={onClear}>
        <X />
      </Button>
      {confirmDialog}
    </div>
  );
}
